import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Flex, Input, InputNumber, Spin, Table, Tag, Upload, notification, UploadProps, Form } from "antd";
import { DeleteFilled, SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";

import { RequirementAddItemUseRepl } from "@/types/requirement";
import { RequirementColumn } from "@/pages/Requirement/type";
import { EditDataWrap, MaterialSearchWrap } from "@/pages/Requirement/style";
import { divided, multi, plus } from "@/utils/calc";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import { ProductSelectMulti } from "@/components/Selector";
import SText from "@/styles/SText";
import CommonTable from "@/components/CommonTable";
import type { ColumnType } from "antd/es/table";
import { list } from "@/apis/inventoryv2";

import { generateTemplate, parseData } from "./utils";
import { Product } from "@/types/product";
import { productList } from "@/apis/product";
import { getRequirementRowKey, numFormmater } from "../../util";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import ExportProgressModal from "@/components/ExportProgressModal";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";

interface RequirementItemProps extends RequirementColumn {
  checked?: boolean;
}

interface EditDataProps {
  purchaseProducts: Array<RequirementItemProps>;
  setPurchaseProducts: Dispatch<SetStateAction<RequirementItemProps[]>>;
  requireCount: number;
  scrollHeight?: number;
  isHandleAdd?: boolean;
  matchStock?: boolean;
  setMatchStock?: (val: boolean) => void;
  matchFactory?: boolean;
  setMatchFactory?: (val: boolean) => void;
  isMerge?: boolean;
  setIsMerge?: (val: boolean) => void;
  locTypes?: number[];
  setLocTypes?: (val: number[]) => void;
}

const EditRequirement: FC<EditDataProps> = ({
  purchaseProducts,
  setPurchaseProducts,
  requireCount,
  scrollHeight = 600,
  isHandleAdd = false,
  matchStock = false,
  setMatchStock = (val: boolean) => { },
  matchFactory = false,
  setMatchFactory = (val: boolean) => { },
  isMerge = false,
  setIsMerge = (val: boolean) => { },
  locTypes = [],
  setLocTypes = (val: number[]) => { },
}) => {

  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const filteredItems = useMemo(() => filterProductItemsByKeyword(purchaseProducts, searchKeyword), [purchaseProducts, searchKeyword]);
  const { onConfirm } = useFrontDelTip<void, [Array<RequirementItemProps>]>({
    cb: (items) => {
      return new Promise((resolve, reject) => {
        setPurchaseProducts(items.filter(item => !item.checked));
        resolve()
      })
    },
    desc: t('requirement.delSubTip')
  });

  const [openProduct, setOpenProduct] = useState<boolean>(false);

  // 排序状态
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null
  });

  // 排序组件
  const SortHeader = ({
    text,
    sortKey
  }: {
    text: string;
    sortKey: string;
  }) => (
    <Flex
      align="center"
      gap={4}
      style={{ cursor: "pointer", userSelect: "none", flex: 1 }}
      onClick={() => {
        let newDirection: "asc" | "desc" | null = "asc";
        if (sortConfig.key === sortKey) {
          if (sortConfig.direction === "asc" || !!!sortConfig.direction) {
            newDirection = 'desc';
          } else if (sortConfig.direction === "desc") {
            newDirection = 'asc'
          }
        }
        setSortConfig({ key: sortKey, direction: newDirection });
      }}
    >
      <SText text={text} />
      {sortConfig.key === sortKey && sortConfig.direction === "asc" && (
        <SortAscendingOutlined style={{ color: "#1890ff" }} />
      )}
      {sortConfig.key === sortKey && sortConfig.direction === "desc" && (
        <SortDescendingOutlined style={{ color: "#1890ff" }} />
      )}
    </Flex>
  );

  // 处理排序逻辑
  const handleSort = (
    data: RequirementItemProps[],
    key: string,
    direction: "asc" | "desc" | null
  ) => {
    if (!direction) return data;

    return [...data].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (key) {
        case "productCode":
          valueA = Number(a.product?.code ?? "") || 0;
          valueB = Number(b.product?.code ?? "") || 0;
          break;
        case "useQuantity":
          valueA = Number(a.useQuantity ?? 0);
          valueB = Number(b.useQuantity ?? 0);
          break;
        case "baseQuantity":
          valueA = Number(a.baseQuantity ?? 0);
          valueB = Number(b.baseQuantity ?? 0);
          break;
        case "attritionRate":
          valueA = Number(a.attritionRate ?? 0);
          valueB = Number(b.attritionRate ?? 0);
          break;
        case "bomRequireCount":
          valueA = Number(a.bomRequireCount ?? 0);
          valueB = Number(b.bomRequireCount ?? 0);
          break;
        case "extCount":
          valueA = Number(a.extCount ?? 0);
          valueB = Number(b.extCount ?? 0);
          break;
        case "useAmount":
          valueA = Number(a.useAmount ?? 0);
          valueB = Number(b.useAmount ?? 0);
          break;
        case "useStock":
          valueA = Number(replaceStockNums?.[a.product?.id ?? ''] ?? 0);
          valueB = Number(replaceStockNums?.[b.product?.id ?? ''] ?? 0);
          break;
        default:
          return 0;
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // 排序 purchaseProducts
  useEffect(() => {
    if (sortConfig.direction && sortConfig.key) {
      const sortedItems = handleSort(purchaseProducts, sortConfig.key, sortConfig.direction);
      if (JSON.stringify(sortedItems.map((i: any) => i.id ?? i.product?.id ?? i.product?.code)) !== JSON.stringify(purchaseProducts.map((i: any) => i.id ?? i.product?.id ?? i.product?.code))) {
        setPurchaseProducts(sortedItems);
      }
    }
  }, [sortConfig]);

  const [stockLoading, setStockLoading] = useState<boolean>(false);
  const [replaceStockNums, setReplaceStockNums] = useState<Record<string, number>>();

  const [uploading, setUploading] = useState<boolean>(false);

  // 下载模版
  const handleSaveTemplate = () => {
    generateTemplate(t);
  };

  // 导入数据
  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        const productListRes = await productList({ params: { strs: addList.map(a => a.product.code) } });
        const productListMap: Record<string, Product> = {};
        productListRes.body.forEach(p => {
          productListMap[p.code] = { ...p }
        });
        let notProsList: string[] = [];
        for (let index = 0; index < addList.length; index++) {
          let code = addList[index].product.code
          if (!!productListMap[code]) {
            addList[index].product = { ...productListMap[code] }
          } else notProsList.push(code)
        }
        // if (notProsList.length > 0) {
        //   notification.error({
        //     message: t('apiError.error26'),
        //     description: notProsList.join(', ')
        //   })
        // }
        // 只添加匹配到产品的数据
        const validAddList = addList.filter(item => !notProsList.includes(item.product.code));
        if (validAddList.length > 0) {
          setPurchaseProducts([...purchaseProducts, ...validAddList]);
          notification.success({
            placement: 'top',
            message: t("requirement.parseDataSuccess", { count: validAddList.length }),
            description: notProsList.length > 0 ? (
              <>
                <div style={{ color: "red" }}>{t('apiError.error26')}</div>
                <span>{notProsList.join(', ')}</span>
              </>
            ) : undefined
          });
        } else {
          notification.error({
            message: t('apiError.error26'),
            description: notProsList.join(', ')
          })
        }
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };
  useEffect(() => {
    const newValues = [...purchaseProducts];
    newValues.forEach((nv, index) => {
      if (!!nv.beReplaced || !!nv.replaceParentId) return;
      const rate = divided(nv.attritionRate.toString(), '100')
      const itemRate = multi(nv.useQuantity.toString(), rate)
      const itemQuantity = plus(nv.useQuantity.toString(), itemRate)
      const bomRequireCount = Math.ceil(Number(multi(itemQuantity, requireCount.toString()) ?? ''))
      newValues[index].bomRequireCount = bomRequireCount;
      newValues[index].useAmount = Math.ceil(Number(plus(bomRequireCount + '', nv.extCount.toString())));
    })

    setPurchaseProducts(newValues);
  }, [requireCount])

  useEffect(() => {
    if (purchaseProducts.length > 0) getStockNum();
  }, [purchaseProducts.length])

  const getStockNum = () => {
    setStockLoading(true);
    let ids = purchaseProducts.map(pro => pro?.product?.id ?? '') || []
    list({ body: { ids: ids.filter(id => !!id) } })
      .then(res => {
        let replaces: Record<string, number> = {};
        res?.body?.forEach(inventory => {
          replaces[inventory.productId] = inventory.count - inventory.preOut;
        });
        setReplaceStockNums({ ...replaces })
      })
      .finally(() => setStockLoading(false))
  }

  const calcData = ({
    row, useQuantity, attritionRate, extCount
  }: {
    row: number,
    useQuantity: number,
    attritionRate: number,
    extCount: number
  }) => {
    const newValues = [...purchaseProducts];
    const rate = divided(attritionRate.toString(), '100')
    const itemRate = multi(useQuantity.toString(), rate)
    const itemQuantity = plus(useQuantity.toString(), itemRate)
    const bomRequireCount = Math.ceil(Number(multi(itemQuantity, requireCount.toString()) ?? ''))

    newValues[row].useQuantity = useQuantity
    newValues[row].attritionRate = attritionRate
    newValues[row].bomRequireCount = bomRequireCount;
    newValues[row].extCount = extCount
    newValues[row].useAmount = Math.ceil(Number(plus(bomRequireCount + '', extCount.toString())));

    setPurchaseProducts(newValues);
  }

  const onChange = (val: boolean) => {
    setPurchaseProducts(() => purchaseProducts.map(item => ({ ...item, checked: val })))
  }

  const columns: ColumnType<RequirementItemProps>[] = [
    {
      key: "sequence",
      title: <SText text={t("purchase.seq")} />,
      width: 80,
      fixed: "left",
      render: (_, record, index) => index + 1,
    },
    {
      key: "selection",
      title: <SText text={`A-${t("common.select")}`} />,
      width: 80,
      fixed: "left",
      render: (_, record, index) => <Checkbox tabIndex={-1} checked={record.checked} onChange={e => {
        setPurchaseProducts(last => {
          let newData = [...last];
          newData[index] = {
            ...newData[index],
            checked: e.target.checked
          }
          return [
            ...newData
          ]
        })
      }} />
    },
    {
      key: "productCode",
      title: <SortHeader text={`B-${t("product.code")}`} sortKey="productCode" />,
      width: 160,
      fixed: "left",
      render(value, row) {
        return <>
          <SText text={row?.product?.code || ''} />
          {!!row?.replaceParentId && <SText text={t('bom.itemReq')} style={{ color: "orange" }} />}
          {!!row.beReplaced && <SText text={t('bom.originBom')} style={{ color: "#449fff" }} />}
        </>

      },
    },
    {
      key: "productName",
      title: <SText text={`C-${t("product.name")}`} />,
      width: 180,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.name || ''} />
      },
    },
    {
      key: "model",
      title: <SText text={`D-${t("product.brand1")}`} />,
      width: 340,
      fixed: "left",
      render: (val, record) => <SText text={record.product?.model || ''} />
    },
    {
      key: "uom",
      title: <SText text={`F-${t("product.uom")}`} />,
      width: 160,
      fixed: "left",
      render(value, row, rowIndex) {
        return <SText text={row?.product?.uom || ''} />
      },
    },
    {
      key: "preReady",
      title: <SText text={`G-${t("product.preReady")}`} />,
      width: 120,
      render(value, row, rowIndex) {
        return (row?.product?.preReady || 0) === 1 ? <Tag color='#87d068' >{t('product.isTrue')}</Tag> : <Tag color='#f50'>{t('product.isFalse')}</Tag>
      },
    },
    {
      key: "useQuantity",
      title: <SortHeader text={`H-${t("requirement.useQuantity")}`} sortKey="useQuantity" />,
      width: 160,
      render: (value, record, row) => (
        (isHandleAdd || true) ? <InputNumber changeOnWheel
          formatter={val => numFormmater(Number(val || 0))}
          disabled={!!record.replaceParentId || !!record.beReplaced} tabIndex={-1} style={{ width: "100%" }} min={0} value={record?.useQuantity} onChange={val => {
            calcData({
              row,
              useQuantity: Number(val),
              attritionRate: record.attritionRate,
              extCount: record.extCount
            })
          }} /> : record?.useQuantity ?? ''
      )
    },
    {
      key: "baseQuantity",
      title: <SortHeader text={`I-${t("bom.itemBase")}`} sortKey="baseQuantity" />,
      width: 160,
      render: (value, record, index) => (
        (isHandleAdd || true) ? <InputNumber changeOnWheel
          formatter={val => numFormmater(Number(val || 0))}
          // disabled={!!record.replaceParentId || !!record.beReplaced} 
          disabled
          tabIndex={-1} style={{ width: "100%" }} min={0} value={record?.baseQuantity} onChange={val => {
            setPurchaseProducts(last => {
              let newData = [...last];
              newData[index] = {
                ...newData[index],
                baseQuantity: Number(val) || 0
              }
              return [...newData]
            })
          }} /> : record?.baseQuantity ?? ''
      )
    },
    {
      key: "attritionRate",
      title: <SortHeader text={`J-${t("bom.itemAttritionRate")}`} sortKey="attritionRate" />,
      width: 160,
      render: (value, record, row) => (
        (isHandleAdd || true) ? <InputNumber changeOnWheel disabled={!!record.replaceParentId || !!record.beReplaced} tabIndex={-1} style={{ width: "100%" }} min={0} value={record?.attritionRate} onChange={val => {
          calcData({
            row,
            useQuantity: record.useQuantity,
            attritionRate: Number(val),
            extCount: record.extCount
          })
        }} suffix="%" /> : record?.attritionRate ?? ''
      )
    },
    {
      key: "bomRequireCount",
      title: (
        <Flex vertical align="center">
          <SortHeader text={`K-${t("requirement.bomData")}`} sortKey="bomRequireCount" />
          <SText text={`K = (H+ (H * (J / 100))) * ${t('requirement.prNum')}`} />
        </Flex>
      ),
      dataIndex: "bomRequireCount",
      width: 240,
      render(value, row, rowIndex) {
        return <SText text={!!row.bomRequireCount ? numFormmater(Number(row.bomRequireCount)) : ''} />
      },
    },
    {
      key: "extCount",
      title: <SortHeader text={`L-${t("requirement.mcPre")}`} sortKey="extCount" />,
      width: 160,
      render: (value, record, index) => <InputNumber changeOnWheel min={0} value={record?.extCount} disabled={!!record.replaceParentId}
        formatter={val => numFormmater(Number(val || 0))}
        onChange={val => {
          if (!!record.beReplaced) {
            setPurchaseProducts(last => {
              let newData = [...last];
              let useAmount = (Number(newData[index]?.useAmount) || 0) - (Number(newData[index]?.extCount) || 0)
              newData[index] = {
                ...newData[index],
                extCount: val ?? 0,
                useAmount: (Number(val) || 0) + useAmount
              }
              return [...newData]
            })
          }
          else calcData({
            row: index,
            useQuantity: record.useQuantity,
            attritionRate: record.attritionRate,
            extCount: Number(val)
          })
        }} />
    },
    {
      key: "useAmount",
      title: (
        <Flex vertical align="center">
          <SortHeader text={`M-${t("requirement.requireCount")}`} sortKey="useAmount" />
          <SText text='M = K + L' />
        </Flex>
      ),
      dataIndex: "useAmount",
      width: 160,
      render: (value, record, index) => (
        <InputNumber changeOnWheel tabIndex={-1} style={{ width: "100%" }} min={0}
          max={!!record.replaceParentId ? (replaceStockNums?.[record?.product?.id ?? ''] || 0) : undefined}
          value={!!record.beReplaced ? Math.max((Number(record?.useAmount) || 0) - (Number(record?.extCount) || 0), 0) : record?.useAmount}
          disabled={!!!record.replaceParentId && !!!record.beReplaced}
          formatter={val => numFormmater(Number(val || 0))}
          addonAfter={
            !!record.beReplaced ? <span>+{record.extCount || 0}</span>
              : undefined
          }
          onChange={val => {
            setPurchaseProducts(last => {
              let newData = [...last];
              newData[index] = {
                ...newData[index],
                useAmount: (Number(newData[index].extCount) || 0) + (Number(val) || 0)
              }
              return [...newData]
            })
          }} />
      )
    },
    {
      key: "useStock",
      title: <SortHeader text={t('bom.useStock')} sortKey="useStock" />,
      width: 160,
      render: (_, row) => (
        <span>{numFormmater(Number(replaceStockNums?.[row?.product?.id ?? ''] ?? '0'))}</span>
      )
    },
  ]

  // 搜索后的显示行号不同，原有编辑回调仍使用源数组行号。
  const searchColumns = columns.map<ColumnType<RequirementItemProps>>(column => {
    const render = column.render;
    return render ? {
      ...column,
      render: (value, record) => render(value, record, purchaseProducts.indexOf(record)),
    } : column;
  });

  return (
    <Spin spinning={stockLoading}>
      <EditDataWrap>
        <Flex gap={6} className="add-row">
          <Checkbox onChange={(e) => onChange(e.target.checked)}>{t('requirement.selectAll')}</Checkbox>
          {purchaseProducts.filter(item => item.checked).length > 0 && <Button
            size="small"
            type="link"
            danger
            icon={<DeleteFilled />}
            onClick={() => onConfirm(purchaseProducts)}
          />}
          <Button type="primary" onClick={() => {
            setOpenProduct(true)
          }}>
            {t("common.add")}
          </Button>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleImport}
            showUploadList={false}
            disabled={uploading}
          >
            <Button loading={uploading}>{t("requirement.inputTemp")}</Button>
          </Upload>
          <Button
            onClick={handleSaveTemplate}
          >
            {t('requirement.downloadTemplate')}
          </Button>
          <Tag>{t("requirement.allCount")}</Tag>
          <Checkbox checked={matchStock} onChange={(e) => {
            let checked = e.target.checked;
            if (checked) {
              setMatchStock(true);
              setMatchFactory(false);
              setLocTypes([1]);
            }
            else setMatchStock(false);
          }}>{t('requirement.skipStock')}</Checkbox>
          <Checkbox checked={matchFactory} disabled={matchStock} onChange={(e) => setMatchFactory(e.target.checked)}>{t('requirement.matchFactory')}</Checkbox>
          <Checkbox checked={isMerge} onChange={(e) => setIsMerge(e.target.checked)}>{t('requirement.skipMerge')}</Checkbox>
        </Flex>
        <Flex className="material-search-row" gap={8} align="center">
          <Form.Item label={t('requirement.matchLocType')}>
            <SelectLocationType disabled={matchStock} value={locTypes} onChange={setLocTypes} mode="multiple" maxTagCount={4} />
          </Form.Item>
          <MaterialSearchWrap>
            <ListFilterBar onSearch={() => setSearchKeyword(keyword)} onReset={() => { setKeyword(""); setSearchKeyword(""); }} showReset={false}>
              {({ submit }) => (
                <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                  placeholder={`${t("product.code")} / ${t("product.name")} / ${t("product.model")}`}
                  aria-label={t("requirement.searchPro")} />
              )}
            </ListFilterBar>
          </MaterialSearchWrap>
        </Flex>
        <CommonTable
          rowKey={getRequirementRowKey}
          dataSource={filteredItems}
          columns={searchColumns}
          scrollHeight={scrollHeight - 180}
        />
        <ProductSelectMulti
          values={purchaseProducts.map((item) => item.product)}
          handleOk={(products) => {
            const items: Array<RequirementColumn> = products.map((item) => ({
              product: item,
              useAmount: 0,
              reqs: [],
              useRepl: RequirementAddItemUseRepl.NOT_USE,
              bomItemId: "",
              bomReplId: "",
              bomRequireCount: 0,
              extCount: 0,
              attritionRate: 0,
              baseQuantity: 0,
              useQuantity: 0,
            }));
            setPurchaseProducts([...purchaseProducts, ...items]);
          }}
          openProduct={openProduct}
          setOpenProduct={setOpenProduct}
        />
      </EditDataWrap>
      <ExportProgressModal visible={uploading} progress={0} title={t('requirement.inputTemp') + "..."} />
    </Spin>
  );
};

export default EditRequirement;
